import React, { useEffect, useState, lazy, Suspense } from 'react'
import { Route, Routes, Navigate, Outlet, useLocation, useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { notification, Modal, ConfigProvider, theme } from 'antd'
// TODO: cant move to the bottom duo to breaking LeaderboardAthlete css and possibly others
import './styles/style.scss'
import './styles/antd-select.css'
// Tailwind utilities load after style.scss so they win over antd by source order.
import './styles/tailwind.css'

import { ConfigContextProvider } from './contexts/config-context'
import { PlanningContextProvider } from './contexts/planning-context'
import ErrorContextProvider from './contexts/error-context'
import { useAuth } from './middlewares/auth/auth-context'

import PrivateRoute from './routes/PrivateRoute'
import AdminRoute from './routes/AdminRoute'
import NotFound from './routes/NotFound'
import { userUpdate } from './middlewares/services/user.services'

import Navbar, { isPreviewPage } from './components/Navbar'
import Home from './components/Home'
import Footer from './components/footer/Footer'
import { AuthModal, AuthPage, AUTH_PATHS } from './components/Auth'

import LoadingScreen from './components/common/LoadingScreen'
import CookiesConsent from './components/CookiesConsent'
import DesignTransitionWelcome from './components/DesignTransition'
import LegacyBar from './components/DesignTransition/LegacyBar'
import {
    EventHub,
    EventHubDescriptionTab,
    EventHubDivisionsTab,
    EventHubWorkoutsTab,
    EventHubPlanningTab,
    EventHubLeaderboardTab,
    EventHubGalleryTab,
    EventHubReviewsTab,
} from './components/EventHub'
import { NewVersionBanner, ChunkErrorBoundary } from './components/AppVersion'
import OrganizerShell, { ORG_SIDE_SLOT_ID } from './components/Organizer/OrganizerShell/OrganizerShell'
import { OrganizerTour } from './components/Organizer/Tours'
import { CreateEventProvider } from './contexts/create-event-context'
import CreateEventHost from './components/MyCompetitions/CreateCompetition/CreateEventHost'
import { socket } from './socket'
import { getMaintenance } from './middlewares/services/maintenance.services'

const Organizer = lazy(() => import('./components/Organizer'))
const Users = lazy(() => import('./components/Organizer/Users'))
const Invoices = lazy(() => import('./components/Organizer/Invoices'))
const BannedList = lazy(() => import('./components/Organizer/BannedList'))
const ReservationHistory = lazy(() => import('./components/Organizer/ReservationHistory'))
const EmailLogs = lazy(() => import('./components/Organizer/EmailLogs'))
const MathTests = lazy(() => import('./components/Organizer/MathTests'))
const OpenEventsCalendar = lazy(() => import('./components/Organizer/OpenEventsCalendar'))
const BuzzersResults = lazy(() => import('./components/BuzzersResults'))
const InvoicesPerOrga = lazy(() => import('./components/Organizer/InvoicesPerOrga'))
const Newsletter = lazy(() => import('./components/Newsletter'))
const ComponentsTest = lazy(() => import('./components/ComponentsTest'))
const ImportRegistrations = lazy(() => import('./components/Organizer/ImportRegistrations'))
const AdminDashboard = lazy(() => import('./components/AdminDashboard'))
const DashboardHome = lazy(() => import('./components/AdminDashboard/pages/DashboardHome'))
const Fees = lazy(() => import('./components/fees/Fees')) // tmp disabled

const MyAccount = lazy(() => import('./components/MyAccount'))
const MyCompetitions = lazy(() => import('./components/MyCompetitions'))
const RegistrationSettingsPage = lazy(() => import('./components/Organizer/RegistrationSettings'))
const OrdersPage = lazy(() => import('./components/Organizer/Orders'))
const LiveRegistrations = lazy(() => import('./components/LiveRegistrations'))
const VolunteerSettingsPage = lazy(() => import('./components/Organizer/VolunteerSettings'))
const VolunteerApplicationsPage = lazy(() => import('./components/Organizer/VolunteerApplications'))
const SpectatorSettings = lazy(() => import('./components/Organizer/SpectatorSettings'))
const SpectatorOrders = lazy(() => import('./components/Organizer/SpectatorOrders'))
const SpectatorRegistration = lazy(() => import('./components/Registration/Spectator'))
const SpectatorComplete = lazy(() => import('./components/Registration/Spectator/SpectatorComplete'))
const Registration = lazy(() => import('./components/Registration'))
const RegistrationComplete = lazy(() => import('./components/RegistrationComplete'))
const VolunteerRegistration = lazy(() => import('./components/Registration/Volunteer'))
const ScoreManagementPage = lazy(() => import('./components/Organizer/Leaderboard'))
const LeaderboardAthlete = lazy(() => import('./components/LeaderboardAthlete'))
const LeaderboardPreview = lazy(() => import('./pages/LeaderboardPreview'))
const TvViewPage = lazy(() => import('./components/Organizer/Leaderboard/TvViewPage'))
const ClaimsPage = lazy(() => import('./components/Organizer/Leaderboard/ClaimsPage'))
const ExternalApiPage = lazy(() => import('./components/Organizer/Leaderboard/ExternalApiPage'))
const OnlineCompetitionScoresForm = lazy(() => import('./components/OnlineCompetitionScoresForm'))
const Qualified = lazy(() => import('./components/ticketing/Qualified'))
const PlanningSettingsPage = lazy(() => import('./components/Organizer/Planning/PlanningSettingsPage'))
const Planning = lazy(() => import('./components/Planning'))
const PlanningTV = lazy(() => import('./components/PlanningTV'))
const PlanningTVConfiguration = lazy(() => import('./components/PlanningTVConfiguration'))
// Live Judging (features/liveJudging): judge app, Head Judge and big screen — mobile-first, no navbar
const LiveJudgePage = lazy(() => import('./features/liveJudging/routes').then((m) => ({ default: m.LiveJudgePage })))
const LiveHeadJudgePage = lazy(() =>
    import('./features/liveJudging/routes').then((m) => ({ default: m.LiveHeadJudgePage })),
)
const LiveScreenPage = lazy(() => import('./features/liveJudging/routes').then((m) => ({ default: m.LiveScreenPage })))
const LiveSpeakerPage = lazy(() =>
    import('./features/liveJudging/routes').then((m) => ({ default: m.LiveSpeakerPage })),
)
const LiveRegiePage = lazy(() => import('./features/liveJudging/routes').then((m) => ({ default: m.LiveRegiePage })))
const LiveSettingsPage = lazy(() =>
    import('./features/liveJudging/routes').then((m) => ({ default: m.LiveSettingsPage })),
)
const EmailSendPage = lazy(() => import('./components/Organizer/Emailing').then((m) => ({ default: m.EmailSendPage })))
const EmailHistoryPage = lazy(() =>
    import('./components/Organizer/Emailing').then((m) => ({ default: m.EmailHistoryPage })),
)
import { athletesContract, volunteersContract } from './components/Organizer/Contracts/contracts.audiences'
const ContractSettingsPage = lazy(() =>
    import('./components/Organizer/Contracts').then((m) => ({ default: m.ContractSettingsPage })),
)
const ContractSigning = lazy(() => import('./components/Registration/ContractSigning'))
const ContractReportsPage = lazy(() =>
    import('./components/Organizer/Contracts').then((m) => ({ default: m.ContractReportsPage })),
)
const CheckIn = lazy(() => import('./pages/CheckIn'))
const PartnerExternalApiConfig = lazy(() => import('./components/PartnerExternalApiConfig'))
const PartnerExternalApiEvent = lazy(() => import('./components/PartnerExternalApiConfig/PartnerExternalApiEvent'))
const Maintenance = lazy(() => import('./components/Maintenance'))

// Static antd APIs (notification.*, message.*, Modal.confirm) render outside the
// React tree and ignore <ConfigProvider theme>; give them the body font too.
ConfigProvider.config({ theme: { token: { fontFamily: 'var(--font-body)' } } })
// Clear the navbar (35px top margin + 70px tall) instead of stacking on it.
notification.config({ top: 120 })

function App() {
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    const { i18n, t } = useTranslation()
    const navigate = useNavigate()

    const authContext = useAuth()

    // Auth modal at every width (kit auth.css: the card stays centred on phones, never a
    // bottom sheet or a full page — William, 30 Sept 2026): only user-initiated logins carry
    // `backgroundLocation` (set by the explicit Log In / Sign Up buttons); the page they were
    // on keeps rendering underneath. System redirects and deep links have no background
    // state and fall through to the standalone <AuthPage/> route instead.
    const backgroundLocation = location.state?.backgroundLocation
    const showAuthModal = AUTH_PATHS.includes(location.pathname) && !!backgroundLocation
    const renderedLocation = showAuthModal ? backgroundLocation : location

    const [processInProgress, setProcessInProgess] = useState(false) // meant for Registration
    const [isLoading, setIsLoading] = useState(false)
    const [maintenanceInfo, setMaintenanceInfo] = useState(null)
    const [isCheckingMaintenance, setIsCheckingMaintenance] = useState(true)

    const RegistrationConfig = () => {
        return {
            processInProgress,
            setProcessInProgess,
        }
    }

    useEffect(() => {
        socket.on('maintenance-updated', (maintenanceData) => {
            setMaintenanceInfo(maintenanceData)
        })

        return () => {
            socket.off('maintenance-updated')
        }
    }, [])

    useEffect(() => {
        const checkMaintenance = async () => {
            try {
                const response = await getMaintenance()
                if (!response.error && response.data) {
                    setMaintenanceInfo(response.data)
                }
            } catch (error) {
                console.error('Error checking maintenance:', error)
            } finally {
                setIsCheckingMaintenance(false)
            }
        }

        checkMaintenance()
    }, [])

    useEffect(() => {
        const timezone = authContext?.user?.timezone

        if (authContext.isLoggedIn) {
            if (timezone) {
                dayjs.tz.setDefault(timezone)
            }
        } else {
            dayjs.tz.setDefault(dayjs.tz.guess())
        }
    }, [authContext?.user?.timezone, authContext?.isLoggedIn])

    useEffect(() => {
        const loginWithToken = async (token) => {
            await authContext.onLoginWithToken(token, setIsLoading)
        }

        const containsSporthub = location.pathname.includes('/sporthub')
        const token = searchParams.get('token')

        if (containsSporthub && token) {
            loginWithToken(token)
        }
    }, [location.pathname, searchParams])

    useEffect(() => {
        const isMyAccountDetails = location.pathname.includes('/my-account/details')
        if (
            !authContext.isLoadingStates &&
            authContext.isLoggedIn &&
            !authContext.isValidAccount() &&
            !isMyAccountDetails
        ) {
            notification.error({ message: t('Action required : complete your account details') })
            navigate('/my-account/details')
        }
    }, [authContext.isLoadingStates, authContext.isLoggedIn, location.pathname])

    useEffect(() => {
        try {
            if (
                !authContext.isLoggedIn ||
                !authContext.user?._id ||
                authContext.user?.systemPreferences?.askedForProfilePicture ||
                authContext.user?.logo
            ) {
                return
            }

            const createdTime = new Date(authContext.user.createdAt).getTime()
            const wasCreatedAtLeastOneDayAgo = Date.now() - createdTime >= 24 * 60 * 60 * 1000

            if (!wasCreatedAtLeastOneDayAgo) {
                return
            }

            Modal.confirm({
                title: t('Complete your profile'),
                content: t("We noticed you don't have a profile picture set. Would you like to add one now?"),
                okText: t('Yes, update it'),
                cancelText: t('Later'),
                onOk: () => {
                    navigate('/my-account/details')
                    updateUserAskedForProfilePicture()
                },
                onCancel: () => {
                    updateUserAskedForProfilePicture()
                },
            })
        } catch (err) {
            console.error('profile picture prompt error', err)
        }
    }, [authContext.isLoggedIn, authContext.user?._id])

    const updateUserAskedForProfilePicture = async () => {
        const user = {
            ...authContext.user,
            systemPreferences: { ...authContext.user?.systemPreferences, askedForProfilePicture: true },
        }
        try {
            await userUpdate(user._id, {
                user,
            })
            authContext.updateUser(user)
        } catch (error) {
            console.error('userUpdate error:', error)
        }
    }

    // --------------------------------- DISABLED TEMPORARLY
    // useEffect(() => {
    //     if (i18n.resolvedLanguage) {
    //         dayjs.locale(i18n.resolvedLanguage)
    //     }
    // }, [i18n.resolvedLanguage])

    const RsnatchOldLeaderboardUrlRedirect = () => {
        const competitionName = window.location.pathname.replace('/leaderboard/', '')
        return <Navigate to={`/${competitionName}/leaderboard`} replace />
    }

    const RedirectOldVolunteers = ({ path }) => {
        const pathsDictionnary = {
            volunteering: 'staff/configuration',
            'volunteering/orders': 'staff/orders',
            'registration/volunteers': 'staff/registration',
        }
        const result = window.location.pathname.replace(path, pathsDictionnary[path])
        return <Navigate to={`${result}${window.location.search}`} replace />
    }
    const RedirectSendComplaints = () => {
        const pathParts = window.location.pathname.split('/')
        const eventNumber = pathParts[1]

        return <Navigate to={`/my-events?action=complaints&event=${eventNumber}`} replace />
    }

    const CustomNavigate = ({ to, ...rest }) => {
        const { search, hash } = useLocation()

        return <Navigate to={`${to}${search}${hash}`} {...rest} />
    }

    // Old top-level admin URLs (bookmarks) → same path inside the /admin shell.
    const AdminLegacyRedirect = () => {
        const { pathname, search, hash } = useLocation()

        return <Navigate to={`/admin${pathname}${search}${hash}`} replace />
    }

    const inAdminShell =
        authContext.isAdmin &&
        (renderedLocation.pathname === '/admin' || renderedLocation.pathname.startsWith('/admin/'))

    if (authContext.isLoadingStates || isCheckingMaintenance) {
        return <LoadingScreen />
    }

    const reservationStored = (() => {
        try {
            return JSON.parse(sessionStorage.getItem('reservationStored') || 'null')
        } catch {
            return null
        }
    })()

    const hasActiveReservation =
        !!reservationStored && !!reservationStored.endTime && Number(reservationStored.endTime) > Date.now()

    if (!authContext?.isAdmin && maintenanceInfo?.isActive && !hasActiveReservation) {
        return <Maintenance message={maintenanceInfo.message} estimatedEndTime={maintenanceInfo.estimatedEndTime} />
    }

    return (
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: '#202020',
                    // Body font (Inter) for all antd components — the token's default
                    // system stack otherwise overrides the body inheritance.
                    fontFamily: 'var(--font-body)',
                },
                components: {
                    Rate: {
                        starColor: '#fbbc04',
                        starBg: '#dbdadf',
                    },
                    // organizer guided tours (components/Organizer/Tours): kit ink, radius 10, soft
                    // floating shadow — alias tokens overridden for Tour only, the global radius is untouched
                    Tour: {
                        colorPrimary: '#272727',
                        colorText: '#272727',
                        colorBgElevated: '#ffffff',
                        borderRadiusLG: 10,
                        boxShadowTertiary: '0 8px 24px rgba(0, 0, 0, 0.12)',
                        closeBtnSize: 22,
                    },
                },
            }}
        >
            {isLoading && <LoadingScreen />}
            <CookiesConsent />
            <DesignTransitionWelcome />
            <NewVersionBanner />
            {/* CreateEventProvider: the create-event modal opens over any page, from the nav, the
                mobile menu, the My Events bar and the footer (hence the footer inside the provider). */}
            <CreateEventProvider>
                {/* .app-shell: a two-column grid under body.osd-on (organizer side menu in the first column,
                    portaled into the slot by OrganizerShell; navbar + pages in the second); plain blocks otherwise. */}
                <div className="app-shell">
                    <div id={ORG_SIDE_SLOT_ID} />
                    <div className="app-shell-main">
                        {/* Admin shell has its own header — hide the public site chrome there (admins only: a non-admin gets the 404 page with the normal chrome). */}
                        {!inAdminShell && (
                            <>
                                <Navbar {...RegistrationConfig()} />
                                <LegacyBar />
                                {/* site guided tours (home page, My events) and their invitation card; the
                                    organizer tours have their own host inside OrganizerShell */}
                                <OrganizerTour scope="site" />
                            </>
                        )}
                        <ChunkErrorBoundary>
                            <ErrorContextProvider>
                                <Routes location={renderedLocation}>
                                    {/* MAIN */}
                                    <Route path="/" element={<Home />} />
                                    <Route path="/connect" element={<AuthPage />} />
                                    <Route path="/sign-up" element={<AuthPage />} />

                                    {/* PUBLIC */}
                                    <Route
                                        path="/leaderboard/:competitionName"
                                        element={<RsnatchOldLeaderboardUrlRedirect />}
                                    />
                                    {['/:eventIdentifier/*', '/sporthub/:eventIdentifier/*'].map((path) => (
                                        <Route key={path} path={path} element={<Outlet />}>
                                            {/* NEW: Event Hub — shared hero + tab nav + <Outlet/> for the active tab.
                                    Replaces the old standalone public EventPage / LeaderboardAthlete / Planning
                                    routes (those components stay on disk as the per-tab source to adapt).
                                    Organizers/admins (canEdit) get the editable "Event Settings" variant of the
                                    same hub: settings navbar, publish toggles and inline editing. */}
                                            {/* OrganizerShell: the side menu for the owner / admin (and the reduced menu of
                                    judges, check-in attendants, partners); a plain <Outlet/> for everyone else. */}
                                            <Route element={<OrganizerShell />}>
                                                <Route element={<EventHub />}>
                                                    <Route path="" element={<EventHubDescriptionTab />} />
                                                    <Route path="divisions" element={<EventHubDivisionsTab />} />
                                                    <Route path="workouts" element={<EventHubWorkoutsTab />} />
                                                    <Route path="planning" element={<EventHubPlanningTab />} />
                                                    <Route path="leaderboard" element={<EventHubLeaderboardTab />} />
                                                    <Route path="gallery" element={<EventHubGalleryTab />} />
                                                    <Route path="reviews" element={<EventHubReviewsTab />} />
                                                </Route>
                                                <Route
                                                    path="check-in-athletes"
                                                    element={<CheckIn target="athletes" />}
                                                />
                                                <Route
                                                    path="check-in-volunteers"
                                                    element={<CheckIn target="volunteers" />}
                                                />
                                                <Route
                                                    path="check-in-spectators"
                                                    element={<CheckIn target="spectators" />}
                                                />
                                            </Route>

                                            {/* Full-screen public pages stay OUTSIDE the hub shell (no hero/tab chrome). */}
                                            <Route path="planning/stream/:tvNumber" element={<PlanningTV />} />
                                            {/* Leaderboard TV: public kiosk, same publish gate and hiding rules as the hub Leaderboard tab */}
                                            <Route path="leaderboard/tv" element={<LeaderboardPreview />} />
                                            {/* Live Judging big screen: public kiosk with ?token=, else a logged-in live role */}
                                            <Route path="live/screen" element={<LiveScreenPage />} />
                                            <Route path="*" element={<NotFound />} />
                                        </Route>
                                    ))}

                                    {/* PRIVATE: LOGGED IN USERS ONLY */}
                                    <Route element={<PrivateRoute />}>
                                        <Route element={<AdminRoute />}>
                                            {/* Admin tools live inside the /admin shell (sidebar + header). The static
                                                "admin" segment outranks /:eventIdentifier, so route order does not matter.
                                                Local Suspense: AdminDashboard is lazy — without this the root Suspense
                                                in index.jsx replaces the whole tree (incl. Auth) and remounts auth on
                                                every first visit to /admin, which raced AdminRoute into a stuck 404. */}
                                            <Route
                                                path="/admin"
                                                element={
                                                    <Suspense fallback={<LoadingScreen />}>
                                                        <AdminDashboard />
                                                    </Suspense>
                                                }
                                            >
                                                <Route index element={<DashboardHome />} />
                                                <Route path="organisations" element={<Organizer />} />
                                                <Route path="invoices" element={<Invoices />} />
                                                <Route path="invoices-per-orga/:orgaId" element={<InvoicesPerOrga />} />
                                                <Route path="banned-list" element={<BannedList />} />
                                                <Route path="reservation-history" element={<ReservationHistory />} />
                                                <Route path="email-logs" element={<EmailLogs />} />
                                                <Route path="math-tests" element={<MathTests />} />
                                                <Route path="open-events-calendar" element={<OpenEventsCalendar />} />
                                                <Route path="users" element={<Users />} />
                                                <Route path="newsletter" element={<Newsletter />} />
                                                <Route path="import-registrations" element={<ImportRegistrations />} />
                                                <Route path="buzzers-results" element={<BuzzersResults />} />
                                                <Route path="test" element={<ComponentsTest />} />
                                                <Route path="*" element={<Navigate to="/admin" replace />} />
                                            </Route>
                                            {[
                                                '/organisations',
                                                '/invoices',
                                                '/invoices-per-orga/:orgaId',
                                                '/banned-list',
                                                '/reservation-history',
                                                '/email-logs',
                                                '/math-tests',
                                                '/open-events-calendar',
                                                '/users',
                                                '/newsletter',
                                                '/import-registrations',
                                                '/test',
                                            ].map((p) => (
                                                <Route key={p} path={p} element={<AdminLegacyRedirect />} />
                                            ))}
                                        </Route>
                                        <Route path="/my-events" element={<MyCompetitions />} />
                                        <Route
                                            path="/my-competitions"
                                            element={<CustomNavigate to="/my-events" replace />}
                                        />
                                        <Route path="/my-account/*" element={<MyAccount />} />
                                        <Route
                                            path="/partner-external-api-config"
                                            element={<PartnerExternalApiConfig />}
                                        />
                                        <Route
                                            path="/partner-external-api-config/:eventId"
                                            element={<PartnerExternalApiEvent />}
                                        />
                                        {/* <Route path="/fees" element={<Fees />} /> */}
                                        {['/:eventIdentifier/*', '/sporthub/:eventIdentifier/*'].map((path) => (
                                            <Route key={path} path={path} element={<Outlet />}>
                                                {/* The old editable EventPage index is gone: the Event Hub (PUBLIC block)
                                        now renders the organizer settings variant itself when canEdit.
                                        Organizer pages live inside OrganizerShell (side menu); the tunnels, the Live
                                        Judging phone surfaces, the TV kiosk and the contract signing pages stay outside. */}
                                                <Route element={<OrganizerShell />}>
                                                    <Route
                                                        path="configuration"
                                                        element={
                                                            <ConfigContextProvider>
                                                                <RegistrationSettingsPage />
                                                            </ConfigContextProvider>
                                                        }
                                                    />
                                                    <Route
                                                        path="volunteering"
                                                        element={<RedirectOldVolunteers path="volunteering" />}
                                                    />
                                                    <Route
                                                        path="staff/configuration"
                                                        element={
                                                            <ConfigContextProvider>
                                                                <VolunteerSettingsPage />
                                                            </ConfigContextProvider>
                                                        }
                                                    />
                                                    <Route
                                                        path="volunteering/orders"
                                                        element={<RedirectOldVolunteers path="volunteering/orders" />}
                                                    />
                                                    <Route
                                                        path="staff/orders"
                                                        element={
                                                            <ConfigContextProvider>
                                                                <VolunteerApplicationsPage />
                                                            </ConfigContextProvider>
                                                        }
                                                    />
                                                    <Route
                                                        path="orders"
                                                        element={
                                                            <ConfigContextProvider>
                                                                <OrdersPage />
                                                            </ConfigContextProvider>
                                                        }
                                                    />
                                                    <Route
                                                        path="live-registrations"
                                                        element={
                                                            <ConfigContextProvider>
                                                                <LiveRegistrations />
                                                            </ConfigContextProvider>
                                                        }
                                                    />
                                                    <Route
                                                        path="statistics"
                                                        element={<CustomNavigate to="/orders" replace />}
                                                    />
                                                    <Route path="emailing" element={<EmailSendPage />} />
                                                    <Route path="emailing/history" element={<EmailHistoryPage />} />
                                                    <Route
                                                        path="leaderboard-editor"
                                                        element={<ScoreManagementPage />}
                                                    />
                                                    <Route path="external-api-config" element={<ExternalApiPage />} />
                                                    <Route path="leaderboard/tv-config" element={<TvViewPage />} />
                                                    <Route
                                                        path="planning/stream-config"
                                                        element={<PlanningTVConfiguration />}
                                                    />
                                                    <Route path="live/settings" element={<LiveSettingsPage />} />
                                                    <Route
                                                        path="planning-editor"
                                                        element={
                                                            <PlanningContextProvider>
                                                                <PlanningSettingsPage />
                                                            </PlanningContextProvider>
                                                        }
                                                    />
                                                    <Route
                                                        path="spectators"
                                                        element={
                                                            <ConfigContextProvider>
                                                                <SpectatorSettings />
                                                            </ConfigContextProvider>
                                                        }
                                                    />
                                                    <Route
                                                        path="spectators/orders"
                                                        element={
                                                            <ConfigContextProvider>
                                                                <SpectatorOrders />
                                                            </ConfigContextProvider>
                                                        }
                                                    />
                                                    <Route path="qualified" element={<Qualified />} />
                                                    <Route path="complaints" element={<ClaimsPage />} />
                                                    <Route
                                                        path="send-complaints"
                                                        element={<RedirectSendComplaints />}
                                                    />

                                                    <Route
                                                        path="contract"
                                                        element={<ContractSettingsPage audience={athletesContract} />}
                                                    />
                                                    <Route
                                                        path="contract-reports"
                                                        element={<ContractReportsPage audience={athletesContract} />}
                                                    />

                                                    <Route
                                                        path="staff/contract"
                                                        element={<ContractSettingsPage audience={volunteersContract} />}
                                                    />
                                                    <Route
                                                        path="staff/contract-reports"
                                                        element={<ContractReportsPage audience={volunteersContract} />}
                                                    />
                                                </Route>

                                                <Route
                                                    path="registration"
                                                    element={<Registration {...RegistrationConfig()} />}
                                                />
                                                <Route
                                                    path="registration/:reservationId"
                                                    element={<RegistrationComplete />}
                                                />
                                                <Route
                                                    path="registration/volunteers"
                                                    element={<RedirectOldVolunteers path="registration/volunteers" />}
                                                />
                                                <Route
                                                    path="staff/registration"
                                                    element={<VolunteerRegistration {...RegistrationConfig()} />}
                                                />
                                                <Route
                                                    path="spectators/registration"
                                                    element={<SpectatorRegistration />}
                                                />
                                                <Route
                                                    path="spectators/registration/:reservationId"
                                                    element={<SpectatorComplete />}
                                                />
                                                {/* Live Judging phone surfaces (the big screen route lives in the public block) */}
                                                <Route path="live/judge" element={<LiveJudgePage />} />
                                                <Route path="live/head-judge" element={<LiveHeadJudgePage />} />
                                                <Route path="live/speaker" element={<LiveSpeakerPage />} />
                                                <Route path="live/regie" element={<LiveRegiePage />} />
                                                <Route path="contract-signing" element={<ContractSigning />} />
                                                <Route
                                                    path="staff/contract-signing"
                                                    element={<ContractSigning isForVolunteers />}
                                                />
                                            </Route>
                                        ))}
                                        <Route
                                            path="/score-manage"
                                            element={
                                                // ponytail: event 3412 judges got the old score-manage link, send them to the judge app
                                                // (Oct 3 2026). Drop once that event is over.
                                                window.location.search === '?6a8db97c1b0514a37b902be2' ? (
                                                    <Navigate to="/3412/live/judge" replace />
                                                ) : (
                                                    <OnlineCompetitionScoresForm />
                                                )
                                            }
                                        />
                                        <Route path="/scoring-athlete" element={<OnlineCompetitionScoresForm />} />
                                        {/* Kept outside the admin shell for the non-admin buzzers account (VITE_BUZZERS_EMAIL). */}
                                        <Route path="/buzzers-results" element={<BuzzersResults />} />
                                    </Route>

                                    {/* FALLBACKS */}
                                    <Route path="/error" element={<NotFound />} />
                                    <Route path="*" element={<NotFound />} />
                                </Routes>
                                {showAuthModal && <AuthModal />}
                                {/* "Create an Event" modal / organization gate, opened in place from any page */}
                                <CreateEventHost />
                            </ErrorContextProvider>
                        </ChunkErrorBoundary>
                    </div>
                </div>
                {/* The homepage map view is a fixed full-height panel: nothing scrolls beneath it, so no footer.
                    The planning editor is a full-screen workspace: same. Kiosk pages (TV, stream, live screen): same. */}
                {!(
                    inAdminShell ||
                    (renderedLocation.pathname === '/' &&
                        new URLSearchParams(renderedLocation.search).get('view') === 'map') ||
                    renderedLocation.pathname.endsWith('/planning-editor') ||
                    isPreviewPage(renderedLocation.pathname)
                ) && <Footer />}
            </CreateEventProvider>
        </ConfigProvider>
    )
}

export default App
